/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-statements */

import { defineStore } from 'pinia'
import type { ComputedRef, Ref } from 'vue'

import { useConversationsStore } from '~/entities/container/conversation/stores/conversations'
import { IDEA_CRUD_API } from '~/entities/content/idea/api/crud-ideas'
import { deleteMedia } from '~/entities/content/media/api/medias'
import { POST_CRUD_API } from '~/entities/content/post/api/crud-posts'
import { QUESTION_CRUD_API } from '~/entities/content/question/api/crud-questions'
import { SURVEY_CRUD_API } from '~/entities/content/survey/api/crud-surveys'
import { WEDO_CRUD_API } from '~/entities/content/wedo/api/crud-wedos'
import { WIKI_CRUD_API } from '~/entities/content/wiki/api/crud-wikis'
import type { IWiki } from '~/entities/content/wiki/interfaces/wiki'
import { WikiVisibility } from '~/entities/content/wiki/interfaces/wiki'

import type { getUsersWhoEndorse as getUsersWhoEndorseService } from '~/features-swym/comment/api/comments'
import type { IAnswer, IComment } from '~/features-swym/comment/interfaces/comment'

import { useContentContextManager } from '~/features-shared/config-manager/composables/context-manager'
import { EContentContext } from '~/features-shared/config-manager/interfaces/contexts'
import {
  createConference,
  createRitual,
  createSurvey,
  createVBrickConference,
  deleteConference,
  fetchSwymContent,
  updateConference,
} from '~/features-shared/content/api/contents'
import type { IConferenceParams, IUpdateParams } from '~/features-shared/content/api/interfaces/params'
import { getContentModelFromSubjectUri } from '~/features-shared/content/api/parser-contents'
import { useContextContentsStore } from '~/features-shared/content/composables/context-contents-store'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { contentsMap, mergeContent } from '~/features-shared/content/stores/contents-db'
import { useContentsStoreV1 } from '~/features-shared/content/stores/v1-contents'
import { useContentsStoreV2 } from '~/features-shared/content/stores/v2-contents'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { useTaggable } from '~/features-shared/tags/composables/taggable'
import type { ITimeline } from '~/features-shared/timeline/interfaces/timeline'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
import type { ServiceTrackerEventOptions } from '~/features-generic/tracker/interfaces/tracker'

import type { SwymResponse } from '~/interfaces/services-dto'

export enum EThreadType {
  COMMUNITY = 'community',
  RITUAL = 'ritual',
}

export enum EFeedMode {
  Large = 'large',
  Compact = 'compact',
  Timeline = 'timeline',
}

export enum EFeedSort {
  Creation = '',
  Update = 'update',
}

// Filter of Me Activity
export enum EActivityFilter {
  All = 'all',
  Content = 'contents',
  Event = 'events',
}

export type THREAD_ID = string
export type CONTENT_ID = SUBJECT_6W_URI
export type MESSAGE_ID = SUBJECT_6W_URI
export type COMMENT_ID = SUBJECT_6W_URI
export type ANSWER_ID = SUBJECT_6W_URI

export const useContentsStore = defineStore('contents', () => {
  const storeContentsV1 = useContentsStoreV1() // Swym
  const storeContentsV2 = useContentsStoreV2() // RTC
  const storeConversations = useConversationsStore()
  const isRTCActivated = useFeatureManager('conversation.rtc')
  const taggable = useTaggable({}) // For global tags

  // #region STATE
  const thread_contents_id: Map<THREAD_ID, Set<CONTENT_ID>> = reactive(new Map())

  const thread_selected = ref('')
  const thread_filter: Ref<IContentModel | ''> = ref('')
  const page = ref(1)
  const limit = ref(8)
  // #endregion

  // #region COMPUTED
  const _key = computed(() => thread_selected.value)

  const currentFeed: ComputedRef<Set<string>> = computed(() => thread_contents_id.get(_key.value) || new Set())

  const currentFeedList = computed(() => [...(currentFeed.value)]
    // eslint-disable-next-line max-nested-callbacks
    .map(id => contentsMap.get(id))
    // eslint-disable-next-line max-nested-callbacks
    .filter(el => el) as IContent[])

  const isActiveConversationFromV2 = computed(() => storeConversations.isConversationFromV2(thread_selected.value))

  const currentTimelineList: ComputedRef<ITimeline[]> = computed(
    () => isActiveConversationFromV2.value
      ? storeContentsV2.currentTimelineList
      : storeContentsV1.currentTimelineList,
  )
  // #endregion

  const mergeMessage = (message: Partial<ITimeline>) => {
    if (isActiveConversationFromV2.value)
      return storeContentsV2.mergeMessage(message)
    else
      return storeContentsV1.mergeMessage(message)
  }

  const fetchTimelineMessages = (
    id = '',
    refine: string[] = [],
    from = '',
  ) => {
    thread_selected.value = id
    page.value = 1

    if (isActiveConversationFromV2.value) {
      return storeContentsV2.fetchTimelineAroundMessage(
        refine,
        '',
        from,
      )
    }
    else {
      return storeContentsV1.fetchTimelineAroundMessage(
        refine,
        '',
        from,
      )
    }
  }

  const fetchMessages = (
    id = '',
    refine: string[] = [],
    query = '',
  ) => {
    thread_selected.value = id
    page.value = 1

    if (isActiveConversationFromV2.value) {
      const conversation = storeConversations.getConversationById(thread_selected.value)
      return storeContentsV2.fetchTimeline(refine, query, conversation?.last_message?.message_id)
    }
    else {
      return storeContentsV1.refreshTimeline(refine, query)
    }
  }

  const fetchNextPage = (
    pageNumber?: number,
    refine: string[] = [],
    query = '',
  ) => {
    page.value = pageNumber || (page.value + 1)

    if (isActiveConversationFromV2.value)
      return storeContentsV2.fetchNextPage(refine, query)
    else
      return storeContentsV1.fetchNextPage(refine, query)
  }

  const { context } = useContentContextManager()
  const { store } = useContextContentsStore(context)

  const createContent = async (
    model: IContentModel,
    community_subject_uri: SUBJECT_6W_URI,
    data: Record<string, unknown>,
    tracker?: ServiceTrackerEventOptions,
  ) => {
    let response: SwymResponse<IContent>
    let isPrivate = false
    switch (model) {
      case IContentModel.Idea:
      case IContentModel.DerivedIdea:
        response = await IDEA_CRUD_API.create({
          community_subject_uri,
          ...data,
        }, tracker)
        break
      case IContentModel.Question:
        response = await QUESTION_CRUD_API.create({
          community_subject_uri,
          ...data,
        }, tracker)
        break
      case IContentModel.Survey:
        response = await createSurvey({
          community_subject_uri,
          ...data,
        }, tracker)
        break
      case IContentModel.Wedo:
        response = await WEDO_CRUD_API.create({
          community_subject_uri,
          ...data,
        }, tracker)
        break
      case IContentModel.Conference:
        response = await createConference({
          community_subject_uri,
          description: data.message,
          ...data,
        }, tracker)
        break
      case IContentModel.Streaming:
        response = await createVBrickConference(
          {
            community_subject_uri,
            description: data.message,
            ...data,
          },
          data.app_join_uri as string,
          tracker,
        )
        break
      case IContentModel.Rituals:
        response = await createRitual(
          {
            description: data.message,
            ...data,
          },
          community_subject_uri,
          tracker,
        )
        break
      case IContentModel.Wiki:
        // @ts-expect-error This is be typed better later
        response = await WIKI_CRUD_API.create({
          community_subject_uri,
          ...data,
        }, tracker)

        isPrivate = [
          WikiVisibility.FromPrivateParent,
          WikiVisibility.Private,
        ].includes((response.result as IWiki).visibility)
        break
      case IContentModel.Post:
      case IContentModel.DerivedPost:
      default:
        response = await POST_CRUD_API.create({
          community_subject_uri,
          ...data,
        }, tracker)
        break
    }

    mergeContent(response.result, true)

    // only insert in feed if the content is published and not private
    if (response.result.published && !isPrivate) {
      store.value?.doInsert(
        context.value === EContentContext.All
          ? 'whatsnew'
          : community_subject_uri,
        [response.result.subject_uri],
      )
    }

    return response
  }
  interface removeContentOptions {
    subjectUri: SUBJECT_6W_URI
    communityUri: SUBJECT_6W_URI
    mustDeleteSubpages?: boolean
    tracker?: ServiceTrackerEventOptions
  }
  const removeContent = async ({ subjectUri, communityUri, mustDeleteSubpages, tracker }: removeContentOptions) => {
    const type = getContentModelFromSubjectUri(subjectUri)
    if (type === IContentModel.Question) {
      await QUESTION_CRUD_API.delete({
        subject_uri: subjectUri.split(':')
          .pop()!,
        community_subject_uri: communityUri,
      }, tracker)
    }
    else if (type === IContentModel.Idea) { await IDEA_CRUD_API.delete({ subject_uri: subjectUri }, tracker) }
    else if (type === IContentModel.Wiki) {
      await WIKI_CRUD_API.delete({
        subject_uri: subjectUri.split(':')
          .pop()!,
        mustDeleteSubpages,
      }, tracker)
    }
    else if (type === IContentModel.Survey) {
      await SURVEY_CRUD_API.delete({
        subject_uri: subjectUri.split(':')
          .pop()!,
        community_subject_uri: communityUri,
      }, tracker)
    }
    else if (type === IContentModel.Wedo) { await WEDO_CRUD_API.delete({ subject_uri: subjectUri }, tracker) }
    else if (type === IContentModel.Conference) { await deleteConference(subjectUri, tracker) }
    else if (type === IContentModel.Media) { await deleteMedia(subjectUri, tracker) }
    else { await POST_CRUD_API.delete({ subject_uri: subjectUri }, tracker) }

    contentsMap.delete(subjectUri)
  }

  /**
   * Method to update a content
   *
   * @param params The params to update
   * @param insert Should the content be inserted into the contents list?
   * @param tracker Optional tracking info
   * @returns The response of the update call
   */
  const updateContent = async (params: IUpdateParams, insert?: boolean, tracker?: ServiceTrackerEventOptions) => {
    let response: SwymResponse<IContent | undefined> = { result: undefined }

    if (params.contentType === IContentModel.Post)
      response = await POST_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)
    else if (params.contentType === IContentModel.Idea)
      response = await IDEA_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)
    else if (params.contentType === IContentModel.Question)
      response = await QUESTION_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)
    else if (params.contentType === IContentModel.Wiki)
      response = await WIKI_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)
    else if (params.contentType === IContentModel.Survey)
      response = await SURVEY_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)
    else if (params.contentType === IContentModel.Conference)
      response = await updateConference(params as IConferenceParams, tracker)
    else if (params.contentType === IContentModel.DerivedIdea)
      response = await IDEA_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)
    else if (params.contentType === IContentModel.DerivedPost)
      response = await POST_CRUD_API.update({ subject_uri: params.subject6wuri, ...params }, tracker)

    if (response.result) {
      mergeContent(response.result, true)
      const isPrivate = [
        WikiVisibility.FromPrivateParent,
        WikiVisibility.Private,
      ].includes((response.result as IWiki).visibility)

      if (insert && !isPrivate) {
        store.value?.doInsert(
          context.value === EContentContext.All
            ? 'whatsnew'
            : response.result.community_subject_uri,
          [response.result.subject_uri],
        )
      }
    }

    return response
  }

  const fetchContent = async (
    subject_uri: SUBJECT_6W_URI,
    metadataOnly?: boolean,
    contentType = '',

    /**
     * Tracker event options.
     * @default undefined
     */
    tracker?: ServiceTrackerEventOptions,
  ): Promise<IContent | undefined> => {
    const response = await fetchSwymContent(
      subject_uri,
      metadataOnly,
      contentType,
      tracker,
    )

    if (response) {
      mergeContent(response, true)
      return contentsMap.get(response.subject_uri)
    }
  }

  const removeMessage = (message: Partial<ITimeline>): Promise<void> => {
    if (isActiveConversationFromV2.value)
      return storeContentsV2.removeMessage(message)
    else
      return storeContentsV1.removeMessage(message)
  }

  const editMessage = async (message: Partial<ITimeline>, event: string) => {
    if (isActiveConversationFromV2.value)
      return storeContentsV2.editMessage(message, event)
    else
      return storeContentsV1.editMessage(message, event)
  }

  const retryMessage = async (message: Partial<ITimeline>) => {
    if (isActiveConversationFromV2.value) {
      // todo retryMessage
      // return storeContentsV2.retryMessage(message)
    }
    else {
      return storeContentsV1.retryMessage(message)
    }
  }

  const addContent = (content: IContent) => {
    if (!content.community_subject_uri)
      return

    if (!currentFeed.value.has(content.subject_uri))
      thread_contents_id.set(_key.value, new Set([content.subject_uri, ...currentFeed.value]))
  }

  const addMessage = async (message: ITimeline) => {
    if (isActiveConversationFromV2.value)
      return storeContentsV2.addMessage(message)
    else
      return storeContentsV1.addMessage(message)
  }

  const createMessage = async (messageHtml: string, conversationId?: string, parentMessage?: ITimeline) => {
    if (isActiveConversationFromV2.value)
      return storeContentsV2.createMessage({ message: messageHtml, parentMessage, conversation_id: conversationId })
    else
      return storeContentsV1.createMessage(messageHtml, parentMessage)
  }

  const createInternalMessage = async (action: string, data: any, subject_uri: SUBJECT_6W_URI) => {
    if (storeConversations.isConversationFromV2(subject_uri))
      return storeContentsV2.createInternalMessage(action, data, subject_uri)
    else
      return Promise.resolve()
  }

  const refreshTimeline = async (refine: string[] = [], query = '') => {
    page.value = 1

    if (isRTCActivated.value) {
      if (storeConversations.threadsId.length > 0) {
        if (isActiveConversationFromV2.value)
          return storeContentsV2.refreshTimeline(refine, query)
        else
          return storeContentsV1.refreshTimeline(refine, query)
      }
      return []
    }
    else {
      return storeContentsV1.refreshTimeline(refine, query)
    }
  }

  // For DM notification navigation
  const refreshTimelineMessageUri = async (
    refine: string[] = [],
    query = '',
    from = '',
  ) => {
    page.value = 1

    if (isRTCActivated.value) {
      if (storeConversations.threadsId.length > 0) {
        if (isActiveConversationFromV2.value) {
          return storeContentsV2.fetchTimelineAroundMessage(
            refine,
            query,
            from,
          )
        }

        else {
          return storeContentsV1.fetchTimelineAroundMessage(
            refine,
            query,
            from,
          )
        }
      }
    }
    else {
      return storeContentsV1.fetchTimelineAroundMessage(
        refine,
        query,
        from,
      )
    }
  }

  const emptyContents = () => {
    thread_contents_id.clear()
    contentsMap.clear()
  }

  const emptyTimeline = () => {
    if (isActiveConversationFromV2.value) {
      // return storeContentsV2.emptyTimeline()
    }
    else {
      return storeContentsV1.emptyTimeline()
    }
  }

  const getComments = async (subject_uri: SUBJECT_6W_URI) => {
    if (isActiveConversationFromV2.value && subject_uri.includes(':message:'))
      return storeContentsV2.getComments(subject_uri)
    else
      return storeContentsV1.getComments(subject_uri)
  }

  const getCommentsStats = async (subject_uri: SUBJECT_6W_URI) => {
    if (isActiveConversationFromV2.value && subject_uri.includes(':message:'))
      return storeContentsV2.getCommentsStats(subject_uri)
    else
      return storeContentsV1.getCommentsStats(subject_uri)
  }

  const addMyEndorse = async (subject_uri: SUBJECT_6W_URI) => {
    if (isActiveConversationFromV2.value && subject_uri.includes(':message:'))
      return storeContentsV2.addMyEndorse(subject_uri, thread_selected.value)
    else
      return storeContentsV1.addMyEndorse(subject_uri)
  }

  const removeMyEndorse = async (subject_uri: SUBJECT_6W_URI, endorse_uri: string) => {
    if (isActiveConversationFromV2.value && subject_uri.includes(':message:')) {
      return storeContentsV2.removeMyEndorse({
        subject_uri,
        endorse_uri,
        thread_id: thread_selected.value,
      })
    }
    else { return storeContentsV1.removeMyEndorse(subject_uri, endorse_uri) }
  }

  const getUsersWhoEndorse = async (params: Parameters<typeof getUsersWhoEndorseService>[0]) => {
    if (isActiveConversationFromV2.value && params.subject_uri.includes(':message:')) {
      const members = storeConversations.getConversationById(thread_selected.value)?.users
      return storeContentsV2.getUsersWhoEndorse({
        subject_uri: params.subject_uri,
        thread_id: thread_selected.value,
        members,
      })
    }
    else { return storeContentsV1.getUsersWhoEndorse(params) }
  }

  const forwardMessage = (subject_uri: SUBJECT_6W_URI, message: ITimeline) => {
    if (storeConversations.isConversationFromV2(subject_uri))
      return storeContentsV2.forwardMessage(subject_uri, message)
    else
      return storeContentsV1.forwardMessage(subject_uri, message)
  }

  const updateComment = (subject_uri: SUBJECT_6W_URI, comment: IComment) => {
    if (storeConversations.isConversationFromV2(subject_uri))
      return storeContentsV2.updateComment(comment)
    else
      return storeContentsV1.updateComment(comment)
  }

  const updateAnswer = (subject_uri: SUBJECT_6W_URI, answer: Partial<IAnswer>) => {
    if (!storeConversations.isConversationFromV2(subject_uri))
      return storeContentsV1.updateAnswer(answer)
  }
  // #endregion

  return {
    // #region STATES
    contentsMap,
    thread_selected,
    thread_filter,
    limit,
    page,

    // Tags
    tags: taggable.tags,
    tagsWithSubjectURIData: taggable.tagsWithSubjectURIData,
    tagsWithoutSubjectURIData: taggable.tagsWithoutSubjectURIData,
    // #endregion

    // #region COMPUTED
    _key,
    currentFeedList,
    currentTimelineList,
    isActiveConversationFromV2,
    // #endregion

    // #region ACTIONS
    addContent,
    createContent,
    removeContent,
    fetchTimelineMessages,
    fetchMessages,
    fetchNextPage,
    fetchContent,
    updateContent,
    createMessage,
    addMessage,
    removeMessage,
    editMessage,
    retryMessage,
    mergeContent,
    mergeMessage,
    refreshTimeline,
    refreshTimelineMessageUri,
    emptyTimeline,
    emptyContents,
    getComments,
    getCommentsStats,
    addMyEndorse,
    removeMyEndorse,
    getUsersWhoEndorse,
    createInternalMessage,
    forwardMessage,
    updateComment,
    updateAnswer,
    // #endregion
  }
})
